import React, { ReactNode, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Logo from "../Logo";
import AuthButton from "../AuthButton";
import SignupButton from "../auth/SignupButton";
import HeaderNavigation from "./HeaderNavigation";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/auth";
import NotificationBell from "@/components/domains/notifications/NotificationBell";
import SearchBar from "@/components/SearchBar";
import { Linkedin, Instagram, FileText, Search, X, Menu } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogClose,
} from "@/components/ui/dialog";

interface HomePageLayoutProps {
  children: ReactNode;
}

const HomePageLayout: React.FC<HomePageLayoutProps> = ({ children }) => {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("navigation");
  const [communityExpanded, setCommunityExpanded] = useState(false);

  // Check if we're on a profile page
  const isProfilePage = location.pathname.startsWith('/profile') || location.pathname.startsWith('/u/');

  const isDashboardPage = location.pathname.includes("/dashboard");
  const isSettingsOrNotificationsPage =
    location.pathname.includes("/settings") ||
    location.pathname.includes("/notifications");

  const showFooter = !isDashboardPage && !isSettingsOrNotificationsPage;

  // Only keep header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setScrolled(scrollTop > 10);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className="min-h-screen w-full bg-transparent animate-fade-in flex flex-col">
      {/* Using the warm-gradient class which is defined in index.css */}

      <header
        className={`fixed top-0 left-0 right-0 z-50 w-full transition-all duration-300 ${
          location.pathname === '/get-verified'
            ? isMobile
              ? "glass-header-enhanced"
              : scrolled 
                ? "glass-header-enhanced"
                : "bg-transparent"
            : isMobile || isProfilePage
              ? "glass-header-enhanced" 
              : scrolled 
                ? "glass-header-enhanced" 
                : "bg-transparent"
        }`}
      >
        <div className="container px-3 sm:px-4 py-3 flex justify-between items-center">
          <div className="flex items-center">
            <Logo />
          </div>

          {/* Centered navigation with search icon - Desktop Only */}
          <div className="flex items-center space-x-4">
            <HeaderNavigation scrolled={scrolled} />
            {/* Search icon - Desktop Only */}
            {!isMobile && (
              <button
                className="p-2 text-gray-600 hover:text-gray-800 transition-colors border border-gray-200 rounded-full"
                onClick={() => setSearchOpen(true)}
                aria-label="Open search"
              >
                <Search size={20} />
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3 sm:space-x-4">
            {user && <NotificationBell />}
            {!isMobile && <AuthButton />}
            {isMobile && <SignupButton />}
            {/* Mobile burger menu button */}
            {isMobile && (
              <button
                className="p-2 text-gray-600 hover:text-gray-800 transition-colors"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open mobile menu"
              >
                <Menu size={24} />
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobile && (
        <Dialog open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <DialogContent className="h-full flex flex-col justify-end items-center max-w-none bg-white/20 backdrop-blur-md border-none shadow-none">
            {/* Custom centered close button at the top */}
            <div className="">
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-md hover:bg-gray-50 transition-colors"
                aria-label="Close mobile menu"
              >
                <X size={16} className="text-gray-500" />
              </button>
            </div>
            
            {/* Center card container with max width */}
            <div className="relative w-full max-w-[700px] bg-white rounded-xl overflow-hidden flex flex-col h-[88vh] md:h-[90vh]">


              {/* Modal content */}
              <div className="flex-1 overflow-y-auto px-4 md:px-6">
                <div className="space-y-3 py-2">
                  <div className="space-y-1 py-2">
                    <Link
                      to="/"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Explore
                    </Link>
                    <Link
                      to="/designers"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Designers
                    </Link>
                    <Link
                      to="/"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Home
                    </Link>
                    <Link
                      to="/pricing"
                      className="flex items-center py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Pricing
                    </Link>
                    
                    {/* Community expandable section */}
                    <div>
                      <button
                        className="flex items-center w-full py-3 px-4 text-base font-medium text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                        onClick={() => setCommunityExpanded(!communityExpanded)}
                      >
                        <span>Community</span>
                        <svg
                          className={`w-4 h-4 ml-2 transition-transform ${communityExpanded ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      
                      {communityExpanded && (
                        <div className="ml-4 mt-2 space-y-1">
                          <a
                            href="https://chat.whatsapp.com/GxGCQpa8S6l0E3i5dKZGQM?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Global
                          </a>
                          <a
                            href="https://chat.whatsapp.com/FEGXfBQSxSZBYiZqsCeKvP?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Paris
                          </a>
                          <a
                            href="https://chat.whatsapp.com/Gp3jN7g9fNQJg0eOSiw4r8?mode=ac_t"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          >
                            WCD Montpellier
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {!user && (
                      <Link
                        to="/auth"
                        className="flex items-center py-3 px-4 text-base font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors border-t border-gray-100 mt-4 pt-4"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        Login
                      </Link>
                    )}
                  </div>
                </div>
              </div>


            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Search Modal - Desktop Only */}
      {!isMobile && (
        <Dialog open={searchOpen} onOpenChange={setSearchOpen}>
          <DialogContent className="max-w-2xl w-full bg-transparent border-none shadow-none p-0">
            {/* Search input positioned at top */}
            <div className="w-full max-w-2xl mx-auto -mt-72">
              <SearchBar />
            </div>
          </DialogContent>
        </Dialog>
      )}

              {/* Add padding to account for the fixed header */}
        {location.pathname !== '/' && <div className="h-[56px]"></div>}

      <main className={`flex-grow relative ${location.pathname === '/' ? '' : 'mb-8'}`}>{children}</main>

      {showFooter && !isMobile && (
        <footer className="relative overflow-hidden bg-black text-white py-9 z-40 flex items-center justify-center bg-[url('/footer-bg.png')] bg-cover bg-center bg-no-repeat">
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0" />
          <div className="container mx-auto px-4 w-full relative z-10">
            <div className="flex flex-col justify-between w-full gap-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-8">
                  <div className="text-white/60 text-sm">
                    World Class Design © {new Date().getFullYear()}
                  </div>

                  <div className="flex items-center">
                    <div className="p-3">
                      <a
                        href="https://www.linkedin.com/company/africans-who-design/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Linkedin size={18} />
                      </a>
                    </div>

                    <div className="p-3">
                      <a
                        href="https://www.instagram.com/africanswhodesign.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Instagram size={18} />
                      </a>
                    </div>

                    <div className="p-3">
                      <a
                        href="https://whatsapp.com/channel/0029Vb3RCut2ZjCoP28ZbP3Y"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6 6.31999C16.8 5.49999 15.8 4.89999 14.7 4.49999C13.6 4.09999 12.5 3.99999 11.3 4.09999C10.1 4.19999 9.00001 4.49999 8.00001 5.09999C7.00001 5.59999 6.20001 6.29999 5.50001 7.19999C4.80001 8.09999 4.30001 9.09999 4.10001 10.2C3.80001 11.3 3.90001 12.4 4.00001 13.5C4.20001 14.6 4.60001 15.7 5.20001 16.7C5.80001 17.7 6.60001 18.5 7.50001 19.2C8.40001 19.9 9.50 20.3 10.6 20.5C11.7 20.7 12.9 20.7 14 20.4C15.1 20.1 16.2 19.7 17.1 19C18 18.3 18.8 17.4 19.3 16.4C19.9 15.4 20.1 14.2 20.2 13.1C20.3 12 20.1 10.8 19.7 9.79999C19.3 8.79999 18.5 7.39999 17.6 6.31999ZM16.8 15.6C16.6 16 16.4 16.3 16 16.6C15.7 16.9 15.3 17 14.9 17.2C14.5 17.3 14.1 17.3 13.7 17.2C13.3 17.1 12.9 17 12.5 16.8C12.1 16.6 11.7 16.4 11.3 16.2C11 15.9 10.6 15.7 10.3 15.4C9.70001 14.9 9.10001 14.4 8.70001 13.8C8.30001 13.2 7.90001 12.5 7.70001 11.9C7.50001 11.2 7.50001 10.5 7.70001 9.89999C7.80001 9.59999 7.90001 9.29999 8.10001 8.99999C8.30001 8.69999 8.50001 8.49999 8.80001 8.29999C9.10001 8.09999 9.40001 7.99999 9.80001 7.99999C9.90001 7.99999 10.1 7.99999 10.2 8.09999C10.3 8.19999 10.4 8.29999 10.5 8.49999C10.6 8.69999 10.7 8.89999 10.8 9.09999C10.9 9.29999 11 9.59999 11.1 9.79999C11.2 9.99999 11.2 10.2 11.3 10.4C11.3 10.6 11.4 10.7 11.3 10.9C11.3 11.1 11.1 11.3 11 11.4C10.9 11.5 10.7 11.7 10.6 11.8C10.5 11.9 10.3 12 10.2 12.2C10.3 12.4 10.4 12.6 10.5 12.7C10.7 13 10.9 13.2 11.1 13.4C11.3 13.6 11.5 13.8 11.8 14C12 14.2 12.2 14.3 12.4 14.4C12.6 14.5 12.7 14.7 12.9 14.7C13.1 14.7 13.1 14.6 13.2 14.5C13.3 14.4 13.5 14.1 13.6 14C13.7 13.9 13.8 13.7 13.9 13.7C14 13.7 14.2 13.6 14.3 13.7C14.5 13.8 14.7 13.9 14.9 14C15.1 14.1 15.3 14.2 15.5 14.3C15.7 14.4 15.9 14.5 16.1 14.6C16.3 14.7 16.4 14.8 16.5 14.9C16.6 15 16.7 15.2 16.7 15.3C16.8 15.4 16.9 15.5 16.8 15.6Z"
                            fill="currentColor"
                          />
                          <path
                            d="M19.3 4.69999C18.4 3.79999 17.4 3.09999 16.2 2.59999C15 2.09999 13.8 1.79999 12.5 1.79999C11.2 1.79999 10 2.09999 8.80001 2.59999C7.60001 3.09999 6.50001 3.89999 5.70001 4.79999C4.90001 5.69999 4.20001 6.79999 3.80001 7.99999C3.30001 9.19999 3.10001 10.5 3.10001 11.8C3.10001 13.1 3.30001 14.3 3.80001 15.5C4.00001 16 4.30001 16.5 4.60001 17L3.50001 21.8L8.50001 20.8C9.00001 21 9.50001 21.2 10 21.4C10.5 21.6 11 21.7 11.5 21.8C12 21.9 12.5 21.9 13 21.9C14.3 21.9 15.6 21.6 16.7 21.1C17.9 20.6 18.9 19.9 19.8 19C20.7 18.1 21.3 17.1 21.8 15.9C22.3 14.7 22.5 13.5 22.5 12.2C22.5 10.9 22.2 9.69999 21.8 8.49999C21.3 7.29999 20.4 5.89999 19.3 4.69999ZM18.2 17.8C17.4 18.6 16.5 19.2 15.5 19.7C14.5 20.2 13.4 20.4 12.3 20.4C11.2 20.4 10.1 20.2 9.10001 19.7C8.50001 19.5 8.00001 19.2 7.40001 18.9L4.80001 19.4L5.40001 17C5.10001 16.5 4.90001 16 4.60001 15.4C4.10001 14.4 3.90001 13.3 3.90001 12.2C3.90001 11.1 4.10001 10 4.60001 9.09999C5.10001 8.09999 5.70001 7.19999 6.50001 6.49999C7.30001 5.79999 8.20001 5.19999 9.20001 4.79999C10.2 4.39999 11.3 4.19999 12.4 4.19999C13.5 4.19999 14.6 4.39999 15.6 4.89999C16.6 5.39999 17.5 5.99999 18.2 6.79999C18.9 7.59999 19.5 8.49999 19.9 9.49999C20.3 10.5 20.5 11.6 20.5 12.7C20.5 13.8 20.3 14.9 19.8 15.9C19.4 16.6 18.8 17.2 18.2 17.8Z"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <div className="flex items-center">
                    <Link
                      to="/privacy"
                      className="text-white hover:text-gray-300 transition text-sm"
                    >
                      Privacy Policy
                    </Link>
                  </div>

                  <span className="mx-2 text-white text-lg">|</span>
                  
                  <div className="flex items-center">
                    <Link
                      to="/terms"
                      className="text-white hover:text-gray-300 transition text-sm"
                    >
                      Terms of Service
                    </Link>
                  </div>

                  <span className="mx-2 text-white text-lg">|</span>

                  <div className="flex items-center">
                    <a
                      href="https://substack.com/@africanswhodesign"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white hover:text-gray-300 transition text-sm"
                    >
                      What is New
                    </a>
                  </div>
                  
                  <span className="mx-2 text-white text-lg">|</span>

                  <div className="flex items-center">
                    <Link
                      to="/learn-more"
                      className="text-white hover:text-gray-300 transition text-sm"
                    >
                      Learn More
                    </Link>
                  </div>

                  <span className="mx-2 text-white text-lg">|</span>
                  
                  <div className="flex items-center">
                    <Link
                      to="/terms"
                      className="text-white hover:text-gray-300 transition text-sm"
                    >
                      Cookies Policy
                    </Link>
                  </div>
                </div>
              
              </div>
            </div>
          </div>
        </footer>
      )}

      {showFooter && isMobile && (
        <footer className="relative overflow-hidden bg-black text-white py-9 z-40 flex items-center justify-center bg-[url('/footer-bg.png')] bg-cover bg-center bg-no-repeat">
          <div className="absolute inset-0 w-full h-full pointer-events-none z-0" />
          <div className="container mx-auto px-4 w-full relative z-10">
            <div className="flex flex-col justify-between w-full gap-8">
              <div className="grid grid-cols-2 gap-x-1 gap-y-2 w-full">
                <div className="flex flex-col gap-2">
                  <span  className="text-white hover:text-gray-300 transition text-[11px] whitespace-nowrap">
                    Products
                  </span>
                  <Link
                    to='/terms'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    Pricing
                  </Link>
                  <Link
                    to='/about'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    About us
                  </Link>

                  <Link
                    to='https://substack.com/@africanswhodesign'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    What is New
                  </Link>

                  <Link
                    to='/learn-more'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    Learn more
                  </Link>
                </div>
                
                <div className="flex flex-col gap-2">
                  <span  className="text-white hover:text-gray-300 transition text-[11px] whitespace-nowrap">
                    Legal
                  </span>
                  <Link
                    to="/privacy"
                    className="text-white/60 hover:text-gray-300 transition text-[11px]"
                  >
                    Privacy Policy
                  </Link>
                  
                  <Link
                    to="/terms"
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    Terms of Service
                  </Link>

                  <Link
                    to="/terms"
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    Cookies Policy
                  </Link>
                  
                </div>
              </div>

              <div className="grid grid-cols-2 gap-x-1 gap-y-2 w-full">
                <div>
                  <div className="flex flex-col gap-2">
                  <span  className="text-white hover:text-gray-300 transition text-[11px] whitespace-nowrap">
                    Community
                  </span>
                  <Link
                    to='/terms'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    WCD Global
                  </Link>
                  <Link
                    to='https://chat.whatsapp.com/FEGXfBQSxSZBYiZqsCeKvP?mode=ac_t'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    WCD Paris
                  </Link>
                  <Link
                    to='https://chat.whatsapp.com/Gp3jN7g9fNQJg0eOSiw4r8?mode=ac_t'
                    className="text-white/60 hover:text-gray-300 transition text-[11px] whitespace-nowrap"
                  >
                    WCD Montpellier
                  </Link>
                </div>
                </div>
                <div>
                  <div className="text-white text-xs">
                    World Class Design © {new Date().getFullYear()}
                  </div>
                  <div className="flex items-center">
                    <div className="p-3 pl-0">
                      <a
                        href="https://www.linkedin.com/company/africans-who-design/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Linkedin size={18} />
                      </a>
                    </div>

                    <div className="p-3">
                      <a
                        href="https://www.instagram.com/africanswhodesign.io/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <Instagram size={18} />
                      </a>
                    </div>

                    <div className="p-3">
                      <a
                        href="https://whatsapp.com/channel/0029Vb3RCut2ZjCoP28ZbP3Y"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-white/60 hover:text-gray-300 transition"
                      >
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M17.6 6.31999C16.8 5.49999 15.8 4.89999 14.7 4.49999C13.6 4.09999 12.5 3.99999 11.3 4.09999C10.1 4.19999 9.00001 4.49999 8.00001 5.09999C7.00001 5.59999 6.20001 6.29999 5.50001 7.19999C4.80001 8.09999 4.30001 9.09999 4.10001 10.2C3.80001 11.3 3.90001 12.4 4.00001 13.5C4.20001 14.6 4.60001 15.7 5.20001 16.7C5.80001 17.7 6.60001 18.5 7.50001 19.2C8.40001 19.9 9.50 20.3 10.6 20.5C11.7 20.7 12.9 20.7 14 20.4C15.1 20.1 16.2 19.7 17.1 19C18 18.3 18.8 17.4 19.3 16.4C19.9 15.4 20.1 14.2 20.2 13.1C20.3 12 20.1 10.8 19.7 9.79999C19.3 8.79999 18.5 7.39999 17.6 6.31999ZM16.8 15.6C16.6 16 16.4 16.3 16 16.6C15.7 16.9 15.3 17 14.9 17.2C14.5 17.3 14.1 17.3 13.7 17.2C13.3 17.1 12.9 17 12.5 16.8C12.1 16.6 11.7 16.4 11.3 16.2C11 15.9 10.6 15.7 10.3 15.4C9.70001 14.9 9.10001 14.4 8.70001 13.8C8.30001 13.2 7.90001 12.5 7.70001 11.9C7.50001 11.2 7.50001 10.5 7.70001 9.89999C7.80001 9.59999 7.90001 9.29999 8.10001 8.99999C8.30001 8.69999 8.50001 8.49999 8.80001 8.29999C9.10001 8.09999 9.40001 7.99999 9.80001 7.99999C9.90001 7.99999 10.1 7.99999 10.2 8.09999C10.3 8.19999 10.4 8.29999 10.5 8.49999C10.6 8.69999 10.7 8.89999 10.8 9.09999C10.9 9.29999 11 9.59999 11.1 9.79999C11.2 9.99999 11.2 10.2 11.3 10.4C11.3 10.6 11.4 10.7 11.3 10.9C11.3 11.1 11.1 11.3 11 11.4C10.9 11.5 10.7 11.7 10.6 11.8C10.5 11.9 10.3 12 10.2 12.2C10.3 12.4 10.4 12.6 10.5 12.7C10.7 13 10.9 13.2 11.1 13.4C11.3 13.6 11.5 13.8 11.8 14C12 14.2 12.2 14.3 12.4 14.4C12.6 14.5 12.7 14.7 12.9 14.7C13.1 14.7 13.1 14.6 13.2 14.5C13.3 14.4 13.5 14.1 13.6 14C13.7 13.9 13.8 13.7 13.9 13.7C14 13.7 14.2 13.6 14.3 13.7C14.5 13.8 14.7 13.9 14.9 14C15.1 14.1 15.3 14.2 15.5 14.3C15.7 14.4 15.9 14.5 16.1 14.6C16.3 14.7 16.4 14.8 16.5 14.9C16.6 15 16.7 15.2 16.7 15.3C16.8 15.4 16.9 15.5 16.8 15.6Z"
                            fill="currentColor"
                          />
                          <path
                            d="M19.3 4.69999C18.4 3.79999 17.4 3.09999 16.2 2.59999C15 2.09999 13.8 1.79999 12.5 1.79999C11.2 1.79999 10 2.09999 8.80001 2.59999C7.60001 3.09999 6.50001 3.89999 5.70001 4.79999C4.90001 5.69999 4.20001 6.79999 3.80001 7.99999C3.30001 9.19999 3.10001 10.5 3.10001 11.8C3.10001 13.1 3.30001 14.3 3.80001 15.5C4.00001 16 4.30001 16.5 4.60001 17L3.50001 21.8L8.50001 20.8C9.00001 21 9.50001 21.2 10 21.4C10.5 21.6 11 21.7 11.5 21.8C12 21.9 12.5 21.9 13 21.9C14.3 21.9 15.6 21.6 16.7 21.1C17.9 20.6 18.9 19.9 19.8 19C20.7 18.1 21.3 17.1 21.8 15.9C22.3 14.7 22.5 13.5 22.5 12.2C22.5 10.9 22.2 9.69999 21.8 8.49999C21.3 7.29999 20.4 5.89999 19.3 4.69999ZM18.2 17.8C17.4 18.6 16.5 19.2 15.5 19.7C14.5 20.2 13.4 20.4 12.3 20.4C11.2 20.4 10.1 20.2 9.10001 19.7C8.50001 19.5 8.00001 19.2 7.40001 18.9L4.80001 19.4L5.40001 17C5.10001 16.5 4.90001 16 4.60001 15.4C4.10001 14.4 3.90001 13.3 3.90001 12.2C3.90001 11.1 4.10001 10 4.60001 9.09999C5.10001 8.09999 5.70001 7.19999 6.50001 6.49999C7.30001 5.79999 8.20001 5.19999 9.20001 4.79999C10.2 4.39999 11.3 4.19999 12.4 4.19999C13.5 4.19999 14.6 4.39999 15.6 4.89999C16.6 5.39999 17.5 5.99999 18.2 6.79999C18.9 7.59999 19.5 8.49999 19.9 9.49999C20.3 10.5 20.5 11.6 20.5 12.7C20.5 13.8 20.3 14.9 19.8 15.9C19.4 16.6 18.8 17.2 18.2 17.8Z"
                            fill="currentColor"
                          />
                        </svg>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default HomePageLayout;
